import React, { createContext, useContext } from "react"

import { Label } from "@coval/ui"

interface FieldLabelProps {
  children: React.ReactNode
  htmlFor?: string
  /** Forwarded to the inner <Label> so it can be referenced via aria-labelledby. */
  id?: string
  required?: boolean
  /**
   * Optional context shown in an info tooltip next to the label. Useful for
   * explaining arguments tied to third-party services.
   */
  tooltip?: React.ReactNode
}

const RequiredMarkerVisibilityContext = createContext(true)

export const RequiredMarkerVisibilityProvider: React.FC<{
  children: React.ReactNode
  visible: boolean
}> = ({ children, visible }) => (
  <RequiredMarkerVisibilityContext.Provider value={visible}>
    {children}
  </RequiredMarkerVisibilityContext.Provider>
)

/**
 * A form field label with an optional required marker and an info tooltip.
 * Renders identically to a plain <Label> when no tooltip is provided.
 */
export const FieldLabel: React.FC<FieldLabelProps> = ({
  children,
  htmlFor,
  id,
  required,
  tooltip,
}) => {
  const showRequiredMarker = useContext(RequiredMarkerVisibilityContext)

  return (
    <div className="flex items-center">
      <Label
        htmlFor={htmlFor}
        id={id}
        tooltipIcon="info"
        tooltip={
          tooltip ? <div className="max-w-xs">{tooltip}</div> : undefined
        }
      >
        {children}
        {required && showRequiredMarker && (
          <span className="text-coval-danger-text"> *</span>
        )}
      </Label>
    </div>
  )
}
